/** A passage to mark in a rendered document: its quote, and how the mark is told apart. */
export interface Highlight {
  key: string;
  quote: string;
  className: string;
}

// Markdown syntax a quote may carry (a phone quotes a block's source) that its rendered text does not.
const IGNORED = /[\s*_`#>~|\\[\]]/;

/**
 * The comparable form of a quote: no link targets, whitespace or markdown markers, so a passage selected
 * across a line break, or quoted as markdown source, still matches its rendered text.
 */
export function matchKey(quote: string): string {
  return [...quote.replace(/…$/, "").replace(/\]\([^)]*\)/g, "")].filter((ch) => !IGNORED.test(ch)).join("");
}

/**
 * highlightQuotes wraps the first occurrence of each quote under `root` in a `<mark data-key>`.
 *
 * A quote spanning several elements is marked piece by piece. Returns the keys that were found, so the
 * caller can tell which comments no longer point at anything in this text.
 */
export function highlightQuotes(root: HTMLElement, highlights: Highlight[]): Set<string> {
  const found = new Set<string>();
  for (const h of highlights) {
    const wanted = matchKey(h.quote);
    if (!wanted) continue;
    const nodes: Text[] = [];
    const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => (n.parentElement?.closest("button, .code-lang") ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    });
    for (let n = walker.nextNode(); n; n = walker.nextNode()) nodes.push(n as Text);
    let flat = "";
    const at: Array<[number, number]> = [];
    nodes.forEach((node, ni) => {
      const text = node.data;
      for (let i = 0; i < text.length; i++) {
        if (IGNORED.test(text[i]!)) continue;
        flat += text[i];
        at.push([ni, i]);
      }
    });
    const start = flat.indexOf(wanted);
    if (start < 0) continue;
    found.add(h.key);
    const end = start + wanted.length - 1;
    const pieces = new Map<number, [number, number]>();
    for (let k = start; k <= end; k++) {
      const [ni, off] = at[k]!;
      const piece = pieces.get(ni);
      pieces.set(ni, piece ? [piece[0], off + 1] : [off, off + 1]);
    }
    for (const [ni, [from, to]] of pieces) {
      const tail = nodes[ni]!.splitText(from);
      tail.splitText(to - from);
      const mark = root.ownerDocument.createElement("mark");
      mark.className = h.className;
      mark.dataset.key = h.key;
      tail.parentNode!.replaceChild(mark, tail);
      mark.appendChild(tail);
    }
  }
  return found;
}
